import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import Button from "../../../component-lib/Molecules/button/Button";
import Input from "../../../component-lib/Atoms/Input/Input";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "708px",
    maxWidth: "none",
  },
}));

interface CreateDocumentPayload {
  file: string;
  name: string;
  description: string;
  type: string;
}

interface CreateDocumentDialogProps {
  isOpen: boolean;
  mode?: "add" | "edit";
  defaultData?: {
    name?: string;
    note?: string;
    file?: string;
    type?: string;
  };
  handleClose: () => void;
  handleSubmit: (payload: CreateDocumentPayload) => void;
}

const IMAGE_TYPES = ["PNG", "JPG", "JPEG", "GIF", "BMP", "SVG", "WEBP"];
// Only PDF and image files are allowed.
const isAllowedFileType = (type: string) =>
  type === "application/pdf" || type.startsWith("image/");
// Limit shown to users in messages and helper text.
const MAX_FILE_SIZE_MB = 10;
// Actual ceiling enforced. A grace band above the advertised limit so files
// only slightly over (e.g. 10.2 MB) still upload. Capped at 12 MB because
// files are sent base64-encoded in JSON (~33% larger), and anything above this
// would push the request past the 20 MB infrastructure limit.
const ENFORCED_MAX_FILE_SIZE_MB = 12;
const ENFORCED_MAX_FILE_SIZE_BYTES = ENFORCED_MAX_FILE_SIZE_MB * 1024 * 1024;

export default function CreateDocumentDialog({
  isOpen = false,
  mode = "add",
  defaultData,
  handleClose,
  handleSubmit,
}: CreateDocumentDialogProps) {
  const isEdit = mode === "edit";
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState(defaultData?.name ?? "");
  const [documentDescription, setDocumentDescription] = useState(
    defaultData?.note ?? "",
  );
  const [error, setError] = useState("");
  const [nameError, setNameError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSetFile = (file: File) => {
    setError("");
    if (!isAllowedFileType(file.type)) {
      setError("Invalid file type. Allowed: PDF or image files.");
      setSelectedFile(null);
      return;
    }
    if (file.size > ENFORCED_MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetFile(file);
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const handleCreate = async () => {
    let hasError = false;
    if (!documentName.trim()) {
      setNameError("Document name is required.");
      hasError = true;
    } else {
      setNameError("");
    }
    // A file is required when creating. When editing, an unchanged file is
    // allowed (the existing file reference is resent).
    if (!selectedFile && !isEdit) {
      setError("Please select a file to upload.");
      hasError = true;
    }
    if (hasError) return;

    try {
      // Keep the existing file/type on edit when no new file is chosen.
      let file = defaultData?.file ?? "";
      let type = defaultData?.type ?? "";
      if (selectedFile) {
        const dataUrl = await convertFileToBase64(selectedFile);
        const base64SizeInBytes = dataUrl.length * 0.75;
        if (base64SizeInBytes > ENFORCED_MAX_FILE_SIZE_BYTES) {
          setError(
            `Encoded file exceeds ${MAX_FILE_SIZE_MB}MB. Please choose a smaller file.`,
          );
          return;
        }
        // Backend requires bare base64 (no "data:<mime>;base64," prefix); the
        // file's MIME is sent separately as `type` so the viewer can rebuild
        // the data URL and preview inline.
        file = dataUrl.includes(",")
          ? dataUrl.substring(dataUrl.indexOf(",") + 1)
          : dataUrl;
        type = selectedFile.type || "";
      }
      handleSubmit({
        file,
        name: documentName.trim(),
        description: documentDescription.trim(),
        type,
      });
    } catch {
      setError("Failed to process the file. Please try again.");
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDialogClose = () => {
    setSelectedFile(null);
    setDocumentName("");
    setDocumentDescription("");
    setError("");
    setNameError("");
    handleClose();
  };

  return (
    <BootstrapDialog
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          handleDialogClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          fontWeight: 600,
          fontSize: "18px",
          fontFamily: "inter",
        }}
        id="customized-dialog-title"
      >
        {isEdit ? "Edit Document" : "Create Document"}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <hr />

      {/* MAIN CONTENT */}
      <DialogContent>
        {/* Document Name Field */}
        <div className="mt-5">
          <Input
            textFieldLabel="Document Name"
            placeholder="Enter document name"
            name="documentName"
            value={documentName}
            onChangeCb={(e) => {
              setDocumentName(e.target.value);
              if (e.target.value.trim()) setNameError("");
            }}
            size="small"
            error={!!nameError}
            helperText={nameError}
          />
        </div>

        {/* Description Field */}
        <div className="mt-5">
          <Input
            textFieldLabel="Description"
            placeholder="Add document reference note"
            name="documentDescription"
            value={documentDescription}
            onChangeCb={(e) => setDocumentDescription(e.target.value)}
            size="small"
          />
        </div>

        {/* File Upload Area */}
        <div className="mt-5">
          <Box
            sx={{
              border: isDragging ? "2px dashed #1976d2" : "2px dashed #C4C4C4",
              borderRadius: "8px",
              padding: "32px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragging ? "#E3F2FD" : "#FAFAFA",
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": { borderColor: "#1976d2" },
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "14px",
                color: isDragging ? "#1976d2" : "#565656",
                marginBottom: "8px",
              }}
            >
              {isDragging
                ? "Drop your file here"
                : isEdit
                  ? "Drag & drop or click to replace the current file (optional)"
                  : "Drag & drop a file here or click to browse"}
            </Typography>
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "12px",
                color: "#9E9E9E",
              }}
            >
              Supported: PDF, Images (Max {MAX_FILE_SIZE_MB}MB)
            </Typography>
          </Box>

          {/* Existing file when editing (shown until a new file is chosen) */}
          {isEdit && !selectedFile && defaultData?.file && (
            <Box
              sx={{
                marginTop: "16px",
                padding: "12px 16px",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "inter",
                  fontSize: "12px",
                  color: "#9E9E9E",
                  marginBottom: "8px",
                }}
              >
                Current file
              </Typography>
              {IMAGE_TYPES.includes((defaultData?.type || "").toUpperCase()) ? (
                <img
                  src={defaultData.file}
                  alt={documentName || "Current file"}
                  style={{
                    maxHeight: 120,
                    maxWidth: "100%",
                    objectFit: "contain",
                    borderRadius: 4,
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontFamily: "inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#1D1D1D",
                  }}
                >
                  {documentName || "Document"}
                  {defaultData?.type ? ` (${defaultData.type})` : ""}
                </Typography>
              )}
            </Box>
          )}

          {selectedFile && (
            <Box
              sx={{
                marginTop: "16px",
                padding: "12px 16px",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#1D1D1D",
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "inter",
                    fontSize: "12px",
                    color: "#9E9E9E",
                  }}
                >
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
              <IconButton onClick={handleRemoveFile} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {error && (
            <Typography
              sx={{
                fontFamily: "inter",
                fontSize: "13px",
                color: "#d32f2f",
                marginTop: "8px",
              }}
            >
              {error}
            </Typography>
          )}
        </div>
      </DialogContent>

      {/* ACTION BUTTONS */}
      <DialogActions style={{ padding: "24px" }}>
        <Button
          label="Cancel"
          variant="outlined"
          onClickCb={handleDialogClose}
          style={{
            width: "120px",
            height: "44px",
            padding: "12px 20px",
            fontSize: "16px",
            backgroundColor: "white",
            border: "none",
            color: "#393939",
          }}
        />
        <Button
          label={isEdit ? "Update" : "Create"}
          variant="contained"
          disabled={(!selectedFile && !isEdit) || !documentName.trim()}
          onClickCb={handleCreate}
          style={{
            padding: "8px 55px",
            fontSize: "16px",
          }}
        />
      </DialogActions>
    </BootstrapDialog>
  );
}
